import { useXHR, type XHRInstance } from "@sembark-travel/xhr"
import { TFormattedMoney } from "@sembark-travel/money"
import useSWR from "swr"

export type TWalletStats = {
	balance: number
	is_exhausted: boolean
	is_low: boolean
}

export type TAdminWalletStats = TWalletStats & {
	consumers: Record<string, { count: number }>
	parser_costs: Array<{ currency: string; total: number }>
}

export type TWalletCreditCreateData = {
	tenant_id: number
	default_credits_per_currency_unit: Record<string, number>
	cost_per_parse: number
}

export type TWalletCreditRecharge = {
	id: number
	tenant_id: number
	amount: number
	amount_money: TFormattedMoney
	currency: string
	credits: number
	note: string | null
	granted_by: {
		id: number
		name: string
		email: string | null
	} | null
	created_at: string | null
}

export type TPaginatedRecharges = {
	data: Array<TWalletCreditRecharge>
	meta?: { current_page: number; last_page: number; total: number }
	links?: { next: string | null; prev: string | null }
}

export function WalletCreditsXHR(xhr: XHRInstance) {
	return {
		async getStats(tenantId: number): Promise<TAdminWalletStats> {
			return xhr
				.get<{
					data: TAdminWalletStats
				}>(`/tenants/${tenantId}/subscription-wallet-credit-stats`)
				.then((r) => r.data.data)
		},
		async getCreateData(tenantId: number): Promise<TWalletCreditCreateData> {
			return xhr
				.get<{
					data: TWalletCreditCreateData
				}>(`/tenants/${tenantId}/subscription-wallet-credit-recharges/create`)
				.then((r) => r.data.data)
		},
		async listRecharges(
			tenantId: number,
			params?: { page?: number; limit?: number }
		): Promise<TPaginatedRecharges> {
			return xhr
				.get<TPaginatedRecharges>(
					`/tenants/${tenantId}/subscription-wallet-credit-recharges`,
					{ params }
				)
				.then((r) => r.data)
		},
		async createRecharge(
			tenantId: number,
			body: {
				amount: number | string
				currency: string
				credits?: number | null
				note?: string | null
			}
		): Promise<TWalletCreditRecharge> {
			return xhr
				.post<{
					data: TWalletCreditRecharge
				}>(`/tenants/${tenantId}/subscription-wallet-credit-recharges`, body)
				.then((r) => r.data.data)
		},
	}
}

export function useWalletCreditCreateData(tenantId: number, enabled: boolean) {
	const xhr = useXHR()
	return useSWR(enabled ? ["wallet-credit-create-data", tenantId] : null, () =>
		WalletCreditsXHR(xhr).getCreateData(tenantId)
	)
}

export function useAdminWalletStats(tenantId: number) {
	const xhr = useXHR()
	return useSWR(["admin-wallet-stats", tenantId], () =>
		WalletCreditsXHR(xhr).getStats(tenantId)
	)
}
